import React, { useState, useMemo, useEffect, useRef } from 'react';
import useLocalization from '../../hooks/useLocalization';
import useSelection from '../../hooks/useSelection';
import { apartmentTableColumns } from '../../helpers/overviewColumnsFields';
import DataTable from '../common/data-table/DataTable';
import UpdateMultipleAppartmentSidebar from './UpdateMultipleAppartmentSidebar';
import { sanitizeFilters } from '../../helpers/global';
import useAuth from '../../hooks/useAuth';
import { apartmentServices } from '../../services/appartmentServices';
import { toast } from 'react-toastify';

const GROUP_STATUS_PAGE_SIZE = 30;

const StatusGroupTable = ({
  status,
  rowKey,
  onRowClick,
  users = [],
  setSelectedApartment,
  selectedApartment,
  showMoreStatuses = false,
  apartmentProfile,
  showArchived,
  getTableFilterValues,
  statusTableTriggerAction,
  setStatusTableTriggerAction,
  onHeaderSelectionInfoChange
}) => {
  const { translate, isRTL } = useLocalization();
  const { user } = useAuth();
  const [apartment, setApartment] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [firstPageLoading, setFirstPageLoading] = useState(false);
  const [morePagesLoading, setMorePagesLoading] = useState(false);
  const [showMultiEditSidebar, setShowMultiEditSidebar] = useState(false);
  const [localTableSortOption, setLocalTableSortOption] = useState({
    key: 'createdAt',
    order: 'desc'
  });

  const {
    selectAllMode,
    onToggleSelectAll,
    onToggleSelectItem,
    isItemSelected,
    selectedItems,
    unselectedItems
  } = useSelection(apartment);

  const onHeaderSelectionInfoChangeRef = useRef(onHeaderSelectionInfoChange);
  useEffect(() => {
    onHeaderSelectionInfoChangeRef.current = onHeaderSelectionInfoChange;
  }, []);

  // Notify parent component about selection info changes
  useEffect(() => {
    if (onHeaderSelectionInfoChangeRef.current) {
      onHeaderSelectionInfoChangeRef.current(status, {
        selectAllMode,
        selectedItems,
        unselectedItems,
        pageInfo,
        showMultiEditSidebar,
        setShowMultiEditSidebar
      });
    }
  }, [selectAllMode, selectedItems, unselectedItems, pageInfo, showMultiEditSidebar, status]);

  const tableColumns = useMemo(
    () =>
      apartmentTableColumns({
        translate,
        users,
        setSelectedApartment,
        selectedApartment,
        selectAllMode,
        onToggleSelectAll,
        onToggleSelectItem,
        isItemSelected,
        showMoreStatuses
      }).map(column => {
        if (column.key === 'status') {
          return {
            ...column,
            disableSearch: true
          };
        }
        return column;
      }),
    [
      translate,
      users,
      selectedApartment,
      selectAllMode,
      onToggleSelectAll,
      onToggleSelectItem,
      isItemSelected,
      showMoreStatuses
    ]
  );

  const [localTableFilterValues, setLocalTableFilterValues] = useState(() => {
    let updatedTableFilterValues = getTableFilterValues(tableColumns, apartmentProfile);
    const filtered = updatedTableFilterValues.filter(f => f?.key !== 'isApartmentArchived');
    return [
      ...filtered,
      { key: 'isApartmentArchived', value: showArchived },
      { key: 'status', value: [status], type: 'text' }
    ];
  });

  useEffect(() => {
    let updatedTableFilterValues = getTableFilterValues(tableColumns, apartmentProfile);
    const filtered = updatedTableFilterValues.filter(f => f?.key !== 'isApartmentArchived');
    setLocalTableFilterValues([
      ...filtered,
      { key: 'isApartmentArchived', value: showArchived },
      { key: 'status', value: [status], type: 'text' }
    ]);
  }, [apartmentProfile, showArchived, status]);

  const fetchApartment = async toLoadPageInfo => {
    try {
      const sanitizedFilters = sanitizeFilters(localTableFilterValues);

      const requestBody = {
        pageSize: toLoadPageInfo.pageSize,
        pageNumber: toLoadPageInfo.pageNumber,
        sort: localTableSortOption,
        filter: sanitizedFilters,
        ignoreEmptyValuesWhileFiltering: Boolean(apartmentProfile)
      };

      const { response, error } = await apartmentServices.getApartments(requestBody);

      if (error) {
        toast.error(error);
        return;
      }

      const { pageNumber: pgNumber = 1, totalPages = 1, resultCount = 0, results = [] } = response || {};

      setPageInfo({
        pageNumber: pgNumber,
        totalPages: totalPages,
        resultCount: resultCount
      });

      if (toLoadPageInfo.pageNumber === 1) {
        setApartment(results);
      } else {
        setApartment(prev => [...prev, ...results]);
      }
    } catch (err) {
      console.error('Failed to fetch apartments:', err);
      toast.error('Could not load apartments');
    }
  };

  const handleBottomReached = async () => {
    if (!pageInfo || !pageInfo.pageNumber || !pageInfo.totalPages) return;

    const { pageNumber, totalPages } = pageInfo;
    const hasMorePages = pageNumber < totalPages;

    if (hasMorePages && !morePagesLoading && !firstPageLoading) {
      setMorePagesLoading(true);

      await fetchApartment({ pageNumber: pageNumber + 1, pageSize: GROUP_STATUS_PAGE_SIZE });

      setMorePagesLoading(false);
    }
  };

  const initialFetch = async () => {
    setFirstPageLoading(true);
    await fetchApartment({ pageNumber: 1, pageSize: GROUP_STATUS_PAGE_SIZE });
    setFirstPageLoading(false);
  };

  useEffect(() => {
    initialFetch();
  }, [localTableFilterValues, localTableSortOption]);

  // Sync with TriggerActionForStatusTable updates (add, update, delete)
  useEffect(() => {
    if (statusTableTriggerAction.trigger && statusTableTriggerAction.status.includes(status)) {
      const refreshTable = async () => {
        await initialFetch();
        if (setStatusTableTriggerAction) {
          setStatusTableTriggerAction({ trigger: false, status: [] });
        }
      };
      refreshTable();
    }
  }, [statusTableTriggerAction.trigger]);

  const handleOnFilterValuesChange = values => {
    const filteredValues = values.filter(
      v => v.key !== 'status' || (!v.value.includes('Show more...') && !v.value.includes('Show less...'))
    );

    setLocalTableFilterValues(filteredValues);
  };

  const handleOnSortChange = sortOption => {
    setLocalTableSortOption(sortOption);
  };

  const handleStatusTableMultiApartmentUpdate = async updates => {
    if (!selectedItems.length && !selectAllMode) {
      toast.error('Please select at least one apartment');
      return;
    }

    setFirstPageLoading(true);

    try {
      const requestBody = {
        apartmentUpdates: updates.apartmentUpdates,
        selectedApartments: selectedItems.map(a => a._id),
        unselectedApartments: unselectedItems.map(a => a._id),
        selectAllMode: selectAllMode,
        filter: localTableFilterValues,
        sort: localTableSortOption
      };

      const { response, error } = await apartmentServices.updateMultipleApartments(requestBody);

      if (error) {
        toast.error(error);
        return;
      }

      if (response) {
        if (response.failedUpdates && response.failedUpdates.length === 0) {
          toast.success('All apartments have been updated');
        } else if (response.failedUpdates && response.failedUpdates.length > 0) {
          toast.warning(`${response.failedUpdates.length} apartments could not be updated. Please check for errors.`);
        }

        // For multi-select updates, refetch to ensure we have the latest data
        await fetchApartment({ pageNumber: 1, pageSize: GROUP_STATUS_PAGE_SIZE });
      }
    } catch (err) {
      console.error('Failed to update apartments:', err);
      toast.error('Could not update apartments');
    } finally {
      setFirstPageLoading(false);
    }
  };

  return (
    <div className={`d-flex gap-2 position-relative w-100 ${showMultiEditSidebar ? 'align-items-stretch' : ''}`}>
      {showMultiEditSidebar && (
        <UpdateMultipleAppartmentSidebar
          assigneUsers={users}
          setShowMultiEditSidebar={setShowMultiEditSidebar}
          onFieldValueSave={handleStatusTableMultiApartmentUpdate}
        />
      )}
      <div
        className={`group-status-table-wrapper ${showMultiEditSidebar ? 'with-sidebar' : ''}`}
        style={{
          height: showMultiEditSidebar ? '100vh' : 'auto',
          marginLeft: showMultiEditSidebar && !isRTL ? '20.5%' : '0',
          marginRight: showMultiEditSidebar && isRTL ? '20.5%' : '0'
        }}
      >
        <div className="group-status-table-inner">
          <DataTable
            maxTableHeight={showMultiEditSidebar ? 'calc(100vh)' : 'calc(100vh - 250px)'}
            rowKey={rowKey}
            columns={tableColumns}
            data={apartment}
            bottomOffset={300}
            onBottomReached={handleBottomReached}
            onRowClick={onRowClick}
            loadingMoreData={morePagesLoading}
            loadingFirstPageData={firstPageLoading}
            sortOptions={localTableSortOption}
            onSortChange={handleOnSortChange}
            filterValues={localTableFilterValues}
            onFilterValuesChange={handleOnFilterValuesChange}
            loaderSize="10"
            headerFontSize="12px"
          />
        </div>
      </div>
    </div>
  );
};

export default StatusGroupTable;
